import { authenticatedFetch } from "@utils/authenticatedFetch";
import { environment } from "../environments/environment";

const MCM_BASE_URL = environment.masterConfigurationManagerURL || process.env.APP_MASTER_CONFIGURATION_MANAGER_URL;
export interface CustomerAgreement {
  agreement_id: number;
  acceptance_status: boolean;
  accepted_on: string | null;
  accepted_by: number | string | null;
  start_date: string | null;
  end_date: string | null;
  created_date: string;
  updated_date: string;
  agreement_name: string;
  agreement_display_name: string;
  agreement_url: string | null;
  agreement_type: string;
  agreement_term: string;
  agreement_duration: number;
  agreement_version: string | null;
  agreement_is_active: boolean;
  mapping_id: number;
}

export interface CustomerAgreementMapping {
  customer_id: number;
  customer_name: string;
  acceptance_date: string | null;
  status: boolean;
  customer_details?: Record<string, unknown> | null;
  total_agreements: number;
  accepted_agreements: number;
  pending_agreements: number;
  agreements: CustomerAgreement[];
}

interface CustomerAgreementMappingsApiResponse {
  success?: boolean;
  message?: string;
  data?: CustomerAgreementMapping[];
  total?: number;
}

export interface FlattenedAgreement {
  mapping_id: number;
  agreement_id: number;
  agreement_display_name: string;
  agreement_url: string | null;
  acceptance_status: boolean;
  start_date: string | null;
}

export interface CustomerAgreementsResult {
  agreements: FlattenedAgreement[];
  total: number;
}

function ensureMcmBaseUrl(): string {
  if (!MCM_BASE_URL) {
    throw new Error(
      "APP_MASTER_CONFIGURATION_MANAGER_URL is not configured. Cannot fetch customer agreements.",
    );
  }
  return MCM_BASE_URL;
}

export async function getCustomerAgreementMappings(
  page = 1,
  size = 100,
  filters?: Record<string, unknown>,
): Promise<CustomerAgreementsResult> {
  const base = ensureMcmBaseUrl();
  const url = new URL("/customer-agreement-mappings", base);

  url.searchParams.set("page", String(page));
  url.searchParams.set("size", String(size));
  if (filters && Object.keys(filters).length > 0) {
    url.searchParams.set("filters", JSON.stringify(filters));
  }

  const response = await authenticatedFetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  const text = await response.text();
  const json: CustomerAgreementMappingsApiResponse = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const message =
      json?.message ||
      `Failed to fetch customer agreement mappings (${response.status} ${response.statusText})`;
    throw new Error(message);
  }

  const customers = Array.isArray(json.data) ? json.data : [];

  const flattened: FlattenedAgreement[] = [];

  for (const customer of customers) {
    const items = Array.isArray(customer.agreements) ? customer.agreements : [];
    for (const a of items) {
      flattened.push({
        mapping_id: a.mapping_id,
        agreement_id: a.agreement_id,
        agreement_display_name: a.agreement_display_name || a.agreement_name,
        agreement_url: a.agreement_url,
        acceptance_status: !!a.acceptance_status,
        start_date: a.start_date,
      });
    }
  }

  const total =
    typeof json.total === "number"
      ? json.total
      : flattened.length;

  return { agreements: flattened, total };
}

export async function acceptCustomerAgreements(customerId: number): Promise<void> {
  const base = ensureMcmBaseUrl();
  const url = new URL("/customer-agreement-mappings/accept", base);
  url.searchParams.set("customer_id", String(customerId));
  url.searchParams.set("accepted_by", String(customerId));

  const response = await authenticatedFetch(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: "{}",
  });

  if (!response.ok) {
    const text = await response.text();
    let message = `Failed to accept agreements (${response.status} ${response.statusText})`;
    try {
      const json = text ? (JSON.parse(text) as { message?: string }) : null;
      if (json?.message) {
        message = json.message;
      }
    } catch {
      // ignore JSON parse error, keep default message
    }
    throw new Error(message);
  }
}
